import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const SECTIONS = [
  {
    id: "what",
    title: "What is hantavirus?",
    body: (
      <>
        <p>
          Hantaviruses are a family of rodent-borne RNA viruses (family <em>Hantaviridae</em>),
          transmitted to humans mainly through inhalation of aerosolised urine, droppings or
          saliva of infected rodents such as deer mice, cotton rats and bank voles.
        </p>
        <p>
          In the Americas they typically cause <strong>Hantavirus Pulmonary Syndrome (HPS / HCPS)</strong>,
          a severe respiratory illness with case fatality rates of 30–40%. In Europe and Asia,
          Old World hantaviruses (Puumala, Seoul, Hantaan, Dobrava-Belgrade) cause{" "}
          <strong>Haemorrhagic Fever with Renal Syndrome (HFRS)</strong>. Andes virus, found in
          Argentina and Chile, is the only hantavirus with documented human-to-human transmission.
        </p>
        <p>
          Common early symptoms include fever, fatigue, severe muscle aches and headache,
          followed in HPS by rapid-onset shortness of breath. There is no specific antiviral
          treatment; supportive care is the mainstay.
        </p>
      </>
    ),
  },
  {
    id: "map",
    title: "What this map shows",
    body: (
      <>
        <p>
          The interactive map plots every reported hantavirus case verified against official
          sources. Each marker is colour-coded by status:
        </p>
        <ul>
          <li><strong>Confirmed</strong> — laboratory-confirmed infection.</li>
          <li><strong>Suspected</strong> — clinically compatible, under investigation.</li>
          <li><strong>Monitoring</strong> — exposure events and contact tracing.</li>
          <li><strong>Fatal</strong> — deaths attributed to hantavirus disease.</li>
        </ul>
        <p>
          The country list expands to show situation notes, official references and recent news
          per region — including the United States, Argentina/Chile (Andes virus), Germany and
          Finland (Puumala), South Korea/China (Hantaan), and the MV Hondius cruise outbreak.
        </p>
      </>
    ),
  },
  {
    id: "stats",
    title: "Global case statistics",
    body: (
      <p>
        Worldwide, an estimated 150,000–200,000 hantavirus infections are reported each year,
        with the majority of HFRS cases concentrated in China, the Korean peninsula and European
        Russia. HPS remains rarer but more lethal, with several hundred cases reported across
        the Americas annually.
      </p>
    ),
  },
  {
    id: "sources",
    title: "Data sources",
    body: (
      <>
        <ul>
          <li>World Health Organization (WHO) — Disease Outbreak News</li>
          <li>US Centers for Disease Control and Prevention (CDC)</li>
          <li>European Centre for Disease Prevention and Control (ECDC)</li>
          <li>UK Health Security Agency (UKHSA)</li>
          <li>National Centre for Infectious Diseases (NCID), Singapore</li>
          <li>PAHO and national ministries of health</li>
          <li>Robert Koch Institute (RKI), Germany</li>
          <li>Peer-reviewed literature in PubMed and The Lancet</li>
        </ul>
        <p className="text-sm text-muted-foreground mt-3">
          Independent open-data project. Not affiliated with WHO, CDC or any government agency.
          For medical advice, contact your local health authority.
        </p>
      </>
    ),
  },
];

export default function SeoContent() {
  return (
    <section
      aria-label="About hantavirus and this tracker"
      className="border-t border-border bg-background text-foreground"
    >
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground font-data mb-2">
            Reference
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">
            About this tracker
          </h2>
        </div>

        <Accordion type="single" collapsible className="border-t border-border">
          {SECTIONS.map((s) => (
            <AccordionItem key={s.id} value={s.id} className="border-b border-border">
              <AccordionTrigger className="text-left text-base md:text-lg font-medium py-5 hover:text-primary hover:no-underline">
                {s.title}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] leading-[1.75] text-foreground/80 space-y-3 pb-6 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:marker:text-muted-foreground [&_strong]:text-foreground [&_strong]:font-semibold">
                {s.body}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
